// src/pages/Listing/PopularLocations.tsx
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, ListGroup, ListGroupItem } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Contractor } from '../../models/Contractor.interface';
import { buildCategoryLocationUrl } from '../../utils/urlUtils';

interface PopularLocationsProps {
  category: string;
  contractors: Contractor[];
}

interface LocationData {
  city: string;
  state: string;
  count: number;
}

const PopularLocations: React.FC<PopularLocationsProps> = ({ 
  category, 
  contractors 
}) => {
  const [popularLocations, setPopularLocations] = useState<LocationData[]>([]);

  useEffect(() => {
    // Extract all locations from contractors in this category
    const locationMap = new Map<string, LocationData>();
    
    contractors.forEach(contractor => {
      if (contractor.searches) {
        contractor.searches.forEach(search => {
          if (search.city && search.state) {
            const locationKey = `${search.city}, ${search.state}`;
            const existingLocation = locationMap.get(locationKey);
            
            if (existingLocation) {
              locationMap.set(locationKey, {
                ...existingLocation,
                count: existingLocation.count + 1
              });
            } else {
              locationMap.set(locationKey, {
                city: search.city,
                state: search.state,
                count: 1
              });
            }
          }
        });
      }
    });

    // Convert to array, sort by count
    const locationsArray = Array.from(locationMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10
    
    setPopularLocations(locationsArray);
  }, [category, contractors]);

  if (popularLocations.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title mb-0">Popular Locations</h5>
      </CardHeader>
      <CardBody>
        <ListGroup flush>
          {popularLocations.map((location, index) => (
            <ListGroupItem key={index} className="border-0 px-0">
              <Link 
                to={buildCategoryLocationUrl(location.state, location.city, category)} 
                className="d-flex justify-content-between align-items-center"
              >
                <span>{location.city}, {location.state}</span>
                <span className="badge bg-light text-dark rounded-pill">{location.count}</span>
              </Link>
            </ListGroupItem>
          ))}
        </ListGroup>
      </CardBody>
    </Card>
  );
};

export default PopularLocations;